import React, { FC, useEffect, useState } from 'react'

import { IConfigMarket } from '@Redux/interfaces'
import { BellIcon } from '@Static/icons/FooterIcons'
import { ChartIcon } from '@Static/icons/HeaderNavigationIcons'
import cn from 'clsx'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { usePriceFlash } from '@App/hooks/component_hooks/useFlash'
import { useInView } from '@App/hooks/useInView'
import { Events } from '@App/sockets/types'

import { selectKlinesForMarket } from '@Redux/slices/config/config.selectors'
import { addFavoriteMarket, setKlines } from '@Redux/slices/config/ConfigSlice'
import { MODAL_TYPES } from '@Redux/slices/modals/enums'
import { setModal } from '@Redux/slices/modals/ModalSlice'
import {
  getTickerData,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@Redux/slices/sockets/PublicServiceConnectionSlice'

import { MarketIcon } from '@UI/crypto_exchange/desktop/components/common/MarketIcon'
import { MarketStatusIcon } from '@UI/crypto_exchange/desktop/components/common/MarketStatusIcon'
import { Button } from '@UI/crypto_exchange/desktop/components/controls/Button'
import { AlertType } from "@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/types";
import { SparkLine } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable/components/SparkLine'
import { ROUTE_NAMES } from '@UI/crypto_exchange/desktop/routes/routeNames'
import classes from '@UI/crypto_exchange/mobi/pages/spot/components/SpotOrderMarketsTable/SpotOrderMarketsTable.module.scss'

import s from './styles.module.scss'

interface Props {
  market: IConfigMarket
  openAlert: (market: IConfigMarket) => void
  alerts: AlertType[]
}

export const CurrencyRateLine: FC<Props> = ({ market, openAlert, alerts }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const tickerData = useAppSelector((state) => getTickerData(state, market.source_currency, market.target_currency))
  const klines = useAppSelector((state) => selectKlinesForMarket(state, market.id))

  const [prevRate, changePrevRate] = useState(tickerData?.close)
  const [percentRate, changePercentRate] = useState(0)
  const [className, changeClassName] = useState('')

  const [ref, inView] = useInView<HTMLTableRowElement>({ threshold: 0 })

  const sparklineData = klines?.data.map((k) => k.close) ?? []
  sparklineData.reverse()

  const sellFlashClass = usePriceFlash(tickerData?.bid, {
    upClass: s.bgBlue,
    downClass: s.bgRed,
    duration: 2000,
  })

  const buyFlashClass = usePriceFlash(tickerData?.ask, {
    upClass: s.bgBlue,
    downClass: s.bgRed,
    duration: 2000,
  })

  const openRemoveModal = (ids: string[]) => {
    dispatch(
      setModal({
        modalType: MODAL_TYPES.REMOVE_ALERT_MODAL,
        data: { ids },
      }),
    )
  }

  useEffect(() => {
    if (inView) {
      if (!klines) {
        dispatch(setKlines(market.id))
      }
      dispatch(
        subscribeToPublicService({
          event: Events.GetRate,
          params: {
            component: 'CurrencyRateBlock',
            source_currency: market.source_currency,
            target_currency: market.target_currency,
          },
        }),
      )
    }

    return () => {
      dispatch(
        unsubscribeToPublicService({
          event: Events.GetRate,
          params: {
            component: 'CurrencyRateBlock',
            source_currency: market.source_currency,
            target_currency: market.target_currency,
          },
        }),
      )
    }
  }, [market.source_currency, market.target_currency, inView])

  useEffect(() => {
    if (tickerData.close !== prevRate) {
      changePrevRate(tickerData.close)

      const percent_rate = prevRate && tickerData ? (100 * (tickerData.close - prevRate)) / tickerData.close : 0
      changePercentRate(percent_rate ? Math.round(1000 * percent_rate) / 1000 : 0)

      if (percent_rate === 0) {
        changeClassName('none')
      } else {
        changeClassName(prevRate > tickerData.close ? classes.red : classes.green)
      }
    }
  }, [tickerData])

  return (
    <div ref={ref} className={cn(s.container, s.selected)}>
      <div className={s.left}>
        <div>
          <div className={s.marketContainer}>
            <MarketStatusIcon market={market} />
            <div className={s.marketImgContainer}>
              <MarketIcon symbol={market.symbol} className={s.marketIcon} />
            </div>
            <div>
              <span className={s.source}>{market.title}</span>
            </div>
          </div>
        </div>
        <div className={`${s.colored} ${className}`}>
          {!Number.isNaN(percentRate) && tickerData.close && sparklineData && (
            <div className={s.sparklineContainer}>
              <div>
                {percentRate > 0 ? '+' : ''} {percentRate}%
              </div>
              <SparkLine sparklineData={sparklineData} percentRate={percentRate} />
            </div>
          )}
        </div>
        <div className={className}>
          <div className={`${s.block}`}>
            <div>
              <span className={cn(s.btn, s.small)}>Sell</span>
            </div>
            <p>
              {tickerData?.bid ? (
                <span className={cn(sellFlashClass, s.value)}>{tickerData.bid}</span>
              ) : (
                <span>&mdash;</span>
              )}
            </p>
          </div>
        </div>
        <div className={className}>
          <div className={`${s.block}`}>
            <div>
              <span className={cn(s.btn, s.small)}>Buy</span>
            </div>
            <p>
              {tickerData.ask ? (
                <span className={cn(buyFlashClass, s.value)}>{tickerData.ask}</span>
              ) : (
                <span>&mdash;</span>
              )}
            </p>
          </div>
        </div>
      </div>
      <div className={s.control}>
        <Button
          variant="ghost"
          className={s.button}
          onClick={() => {
            navigate(ROUTE_NAMES.CHARTS)
            dispatch(addFavoriteMarket(market))
          }}
        >
          <span className={s.iconContainer}>
            <ChartIcon width={16} height={16} />
          </span>
          Go to charts
        </Button>

        <Button variant="ghost" className={s.button} onClick={() => openAlert(market)}>
          <span className={s.iconContainer}>
            <BellIcon width={16} height={16} />
          </span>
          Alerts
        </Button>

        <Button variant="outlined"
                onClick={() => openRemoveModal(alerts
                  .filter(alert => String(alert.market_id) === String(market.id))
                  .map(alert => alert.uuid))}
                className={cn(s.button, s.buttonRemove)}>
          Remove all
        </Button>
      </div>
    </div>
  )
}
